import React, { useMemo, useState } from 'react';
import { Plus, ArrowLeft, X, Users, Send, Calendar, Repeat, Info, Search } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Card, FieldLabel, inputClass, textareaClass, SegmentedControl, StatusPill } from './walletUi';
import { LockScreenPreview, WalletPlatform } from './WalletPassPreview';
import { walletNotifications, WalletNotification, walletTiers, personalisationFields, fillPlaceholders, walletKpis } from '../../data/mockDigitalWallet';
type Audience = 'everyone' | 'tiers' | 'club' | 'filters' | 'members';
type When = 'now' | 'schedule' | 'repeat';
const filterOptions = ['Points', 'Rewards available', 'Last order', 'Joined loyalty', 'Customer tag'];
const sampleMembers = ['Sarah Johnson', 'James Chen', 'Emma Wilson', 'David Park', 'Sophie Miller', 'Liam Brown'];
function Composer({
  onCancel,
  onDone
}: {
  onCancel: () => void;
  onDone: (n: WalletNotification, msg: string) => void;
}) {
  const [title, setTitle] = useState('Hi {{firstName}}, the spring release is here');
  const [message, setMessage] = useState('Members get first access to our new Riesling this week. You have {{points}} points to spend.');
  const [fallbackTitle, setFallbackTitle] = useState('The spring release is here');
  const [fallbackMessage, setFallbackMessage] = useState('Members get first access to our new Riesling this week.');
  const [focus, setFocus] = useState<'title' | 'message'>('message');
  const [audience, setAudience] = useState<Audience>('everyone');
  const [tiers, setTiers] = useState<string[]>(['sommelier', 'vintner']);
  const [club, setClub] = useState<'club' | 'loyalty'>('club');
  const [filters, setFilters] = useState<{
    field: string;
    op: string;
    value: string;
  }[]>([{
    field: 'Points',
    op: 'at least',
    value: '1000'
  }]);
  const [members, setMembers] = useState<string[]>(['Sarah Johnson']);
  const [memberSearch, setMemberSearch] = useState('');
  const [when, setWhen] = useState<When>('now');
  const [date, setDate] = useState('2026-11-08');
  const [time, setTime] = useState('09:00');
  const [repeatType, setRepeatType] = useState('Weekly on chosen days');
  const [repeatDays, setRepeatDays] = useState<string[]>(['Fri']);
  const [endType, setEndType] = useState<'count' | 'date'>('count');
  const [endCount, setEndCount] = useState('12');
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [platform, setPlatform] = useState<WalletPlatform>('apple');
  const [reviewOpen, setReviewOpen] = useState(false);
  const hasPlaceholders = /{{/.test(title + message);
  const count = useMemo(() => {
    if (audience === 'everyone') return walletKpis.inWallet;
    if (audience === 'tiers') return walletTiers.filter(t => tiers.includes(t.id)).reduce((s, t) => s + Math.round(t.members * 0.69), 0);
    if (audience === 'club') return club === 'club' ? 402 : 460;
    if (audience === 'filters') return Math.max(12, 318 - filters.length * 61);
    return members.length;
  }, [audience, tiers, club, filters, members]);
  const audienceLabel = audience === 'everyone' ? 'Everyone with a pass' : audience === 'tiers' ? `Tiers: ${walletTiers.filter(t => tiers.includes(t.id)).map(t => t.name).join(', ') || 'none'}` : audience === 'club' ? club === 'club' ? 'Club members' : 'Loyalty only (not in a club)' : audience === 'filters' ? filters.map(f => `${f.field} ${f.op} ${f.value}`).join(', ') : `${members.length} selected member${members.length === 1 ? '' : 's'}`;
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    if (focus === 'title') setTitle(t => t + token);else setMessage(m => m + token);
  };
  const whenLabel = when === 'now' ? 'Now' : when === 'schedule' ? `${new Date(date).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })}, ${time}` : `${repeatType}${repeatType === 'Weekly on chosen days' ? ` (${repeatDays.join(', ')})` : ''} at ${time}`;
  const submit = () => {
    const n: WalletNotification = {
      id: `n${Date.now()}`,
      title,
      message,
      audience: audienceLabel,
      when: when === 'now' ? 'Just now' : whenLabel,
      status: when === 'now' ? 'Sending' : when === 'repeat' ? 'Repeating' : 'Scheduled',
      passes: count,
      appleSends: 0,
      googleSends: 0,
      repeat: when === 'repeat' ? `${whenLabel}, 0 of ${endCount} sent` : undefined
    };
    onDone(n, when === 'now' ? `Sending to ${count.toLocaleString()} passes` : when === 'repeat' ? 'Repeating notification set up' : `Scheduled for ${whenLabel}`);
  };
  const radio = (value: Audience, label: string, help: string, children?: React.ReactNode) => <div className={`border rounded-lg transition-colors ${audience === value ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0]'}`} data-id="element-2572" data-mp="3q872r7ucu7x3">
      <label className="flex items-start gap-3 p-3 cursor-pointer" data-id="element-2573" data-mp="15g50w391dlkh">
        <input type="radio" name="audience" checked={audience === value} onChange={() => setAudience(value)} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-2574" data-mp="15hzmljs4thpm" />
        <div data-id="element-2575" data-mp="1ki536jtdalrb">
          <div className="text-sm font-medium text-[#0F172A]" data-id="element-2576" data-mp="1kkr8ecsscp6d">{label}</div>
          <div className="text-xs text-[#64748B]" data-id="element-2577" data-mp="1fkv2u94fwpop">{help}</div>
        </div>
      </label>
      {audience === value && children && <div className="px-3 pb-3 pl-9" data-id="element-2578" data-mp="1aqqvhor9ifn3">{children}</div>}
    </div>;
  return <div data-id="element-2579" data-mp="16qgqaebant9a">
      <div className="flex items-center gap-3 mb-6" data-id="element-2580" data-mp="16qqhu6bzm8h3">
        <Button variant="ghost" size="sm" iconLeft={<ArrowLeft className="w-4 h-4" data-id="element-2582" data-mp="11tqcjxmyp9r1" />} onClick={onCancel} data-id="element-2581" data-mp="11rvm63sx8pfk">
          Back to notifications
        </Button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 items-start" data-id="element-2583" data-mp="wv5b5uvz4k10">
        <div className="space-y-6" data-id="element-2584" data-mp="ru98fkncz3x6">
          <Card title="Message" description="Shows on the lock screen and replaces the Notifications field on the back of the pass." data-id="element-2585" data-mp="nsnmzawrdhbd">
            <div className="space-y-4" data-id="element-2586" data-mp="2m6z2q3hn2f53">
              <div data-id="element-2587" data-mp="2m5w93nsxlylh">
                <FieldLabel data-id="element-2588" data-mp="2i6oknsgkny9v">Title</FieldLabel>
                <input className={inputClass} value={title} onFocus={() => setFocus('title')} onChange={e => setTitle(e.target.value)} data-id="element-2589" data-mp="2i7q7svav4b7y" />
              </div>
              <div data-id="element-2590" data-mp="ka6osggopxl8">
                <FieldLabel data-id="element-2591" data-mp="kaq3zpeya7vk">Message</FieldLabel>
                <textarea rows={3} className={textareaClass} value={message} onFocus={() => setFocus('message')} onChange={e => setMessage(e.target.value)} data-id="element-2592" data-mp="kcjg3k8c1kay" />
                <div className="flex items-center justify-between mt-1" data-id="element-2593" data-mp="aloqc5vvo2kr">
                  <div className="flex flex-wrap gap-1.5 items-center" data-id="element-2594" data-mp="yahhzq5gvo9k">
                    <span className="text-xs text-[#64748B] mr-1" data-id="element-2595" data-mp="y8erz64bdv4u">Insert into {focus}:</span>
                    {personalisationFields.map(f => <button key={f.key} type="button" onClick={() => insert(f.key)} className="px-2 py-0.5 text-xs rounded border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]" data-id="element-2596" data-mp="p9t9fayueg42">
                        {f.label}
                      </button>)}
                  </div>
                  <span className="text-xs text-[#94A3B8]" data-id="element-2597" data-mp="3a24xrt5zyrhm">{message.length} / 180</span>
                </div>
              </div>
              {hasPlaceholders && <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-id="element-2598" data-mp="36gtdz3v6bgwh">
                  <div className="flex items-start gap-2 text-xs text-[#475569]" data-id="element-2599" data-mp="32f7siu4kpuao">
                    <Info className="w-4 h-4 text-[#0EA5E9] flex-shrink-0" data-id="element-2600" data-mp="3ggk7b27zzpuz" />
                    Your message is personalised. Add a version without personal fields, used for any member we don't have the details for.
                  </div>
                  <div data-id="element-2601" data-mp="2nrwg6q0ki69i">
                    <FieldLabel data-id="element-2602" data-mp="2np0k778a5rnp">Fallback title</FieldLabel>
                    <input className={inputClass} value={fallbackTitle} onChange={e => setFallbackTitle(e.target.value)} data-id="element-2603" data-mp="2itl7wjpx83te" />
                  </div>
                  <div data-id="element-2604" data-mp="2xizfpndumd6o">
                    <FieldLabel data-id="element-2605" data-mp="2spxyv9u303kg">Fallback message</FieldLabel>
                    <textarea rows={2} className={textareaClass} value={fallbackMessage} onChange={e => setFallbackMessage(e.target.value)} data-id="element-2606" data-mp="2sod69prbuup8" />
                  </div>
                </div>}
            </div>
          </Card>

          <Card title="Who gets it" action={<span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0369A1] bg-[#E0F2FE] px-3 py-1 rounded-full" data-id="element-2608" data-mp="1ejiu2iomzk50">
                <Users className="w-4 h-4" data-id="element-2609" data-mp="1jhsnanqswanz" /> {count.toLocaleString()} passes
              </span>} data-id="element-2607" data-mp="1eih83j631d8i">
            <div className="space-y-2" data-id="element-2610" data-mp="1ofc3ht726xik">
              {radio('everyone', 'Everyone with a pass', `All ${walletKpis.inWallet.toLocaleString()} members who have the pass in a wallet.`)}
              {radio('tiers', 'By tier', 'Pick one or more tiers.', <div className="flex flex-wrap gap-2" data-id="element-2611" data-mp="22g51inwkkaqf">
                  {walletTiers.map(t => {
                const on = tiers.includes(t.id);
                return <button key={t.id} type="button" onClick={() => setTiers(prev => on ? prev.filter(x => x !== t.id) : [...prev, t.id])} className={`flex items-center gap-2 px-3 h-8 rounded-md border text-sm ${on ? 'border-[#0EA5E9] bg-white text-[#0369A1]' : 'border-[#E2E8F0] bg-white text-[#475569]'}`} data-id="element-2612" data-mp="277wt91rgomi5">
                        <span className="w-3 h-3 rounded-sm" style={{
                    background: t.background
                  }} data-id="element-2613" data-mp="2l4ti4xo0rtwe" />
                        {t.name}
                      </button>;
              })}
                </div>)}
              {radio('club', 'Club members or loyalty only', 'Members in a wine club, or members who only have loyalty.', <SegmentedControl value={club} onChange={setClub} options={[{
              value: 'club',
              label: 'Club members'
            }, {
              value: 'loyalty',
              label: 'Loyalty only'
            }]} data-id="element-2614" data-mp="3rpp9xfzpb23h" />)}
              {radio('filters', 'Build an audience', 'Filter on what WinePik knows about your members.', <div className="space-y-2" data-id="element-2615" data-mp="jg5rrs6bmefo">
                  {filters.map((f, i) => <div key={i} className="flex items-center gap-2" data-id="element-2616" data-mp="ejfbp4uiniu9" data-mpi={i}>
                      <select className={`${inputClass} w-44`} value={f.field} onChange={e => setFilters(prev => prev.map((x, j) => j === i ? {
                  ...x,
                  field: e.target.value
                } : x))} data-id="element-2617" data-mp="1by1e5nwfbfm9">
                        {filterOptions.map(o => <option key={o} data-id="element-2618" data-mp="9s3iw413zot1">{o}</option>)}
                      </select>
                      <select className={`${inputClass} w-36`} value={f.op} onChange={e => setFilters(prev => prev.map((x, j) => j === i ? {
                  ...x,
                  op: e.target.value
                } : x))} data-id="element-2619" data-mp="4tsazyudjodg">
                        <option data-id="element-2620" data-mp="esraorx5w9ob">at least</option>
                        <option data-id="element-2621" data-mp="spo0qr5ghn45">less than</option>
                        <option data-id="element-2622" data-mp="socumkojg39v">more than</option>
                        <option data-id="element-2623" data-mp="ns4kjlp7709k">within the last</option>
                        <option data-id="element-2624" data-mp="npiexrlvautz">is</option>
                      </select>
                      <input className={`${inputClass} w-32`} value={f.value} onChange={e => setFilters(prev => prev.map((x, j) => j === i ? {
                  ...x,
                  value: e.target.value
                } : x))} data-id="element-2625" data-mp="12fpqrohddflp" />
                      <button type="button" onClick={() => setFilters(prev => prev.filter((_, j) => j !== i))} className="p-2 text-[#94A3B8] hover:text-[#0F172A]" aria-label="Remove filter" data-id="element-2626" data-mp="17fc21sdn0lrv">
                        <X className="w-4 h-4" data-id="element-2627" data-mp="1q1tcueoo3j7b" />
                      </button>
                    </div>)}
                  <Button variant="ghost" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-2629" data-mp="1h1whxvbopo3t" />} onClick={() => setFilters(prev => [...prev, {
                field: 'Rewards available',
                op: 'at least',
                value: '1'
              }])} data-id="element-2628" data-mp="1v0cw27bvmgvz">
                    Add filter
                  </Button>
                </div>)}
              {radio('members', 'Specific members', 'Search and pick members.', <div data-id="element-2630" data-mp="ycghf8001vne">
                  <div className="relative mb-2" data-id="element-2631" data-mp="yd9d9dxf70qr">
                    <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2632" data-mp="yadfpqphie2u" />
                    <input className={`${inputClass} pl-9`} placeholder="Search by name or email" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} data-id="element-2633" data-mp="12byw3m189ahs" />
                  </div>
                  {memberSearch && <div className="border border-[#E2E8F0] rounded-md bg-white mb-2" data-id="element-2634" data-mp="tf7yh5711nl3">
                      {sampleMembers.filter(m => m.toLowerCase().includes(memberSearch.toLowerCase()) && !members.includes(m)).map(m => <button key={m} type="button" onClick={() => {
                  setMembers(prev => [...prev, m]);
                  setMemberSearch('');
                }} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC]" data-id="element-2635" data-mp="2ma8tglofk1t5">
                          {m}
                        </button>)}
                    </div>}
                  <div className="flex flex-wrap gap-2" data-id="element-2636" data-mp="3a0v2oga67s4q">
                    {members.map(m => <span key={m} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#E2E8F0] rounded-md text-sm" data-id="element-2637" data-mp="3507bnulxzbai">
                        {m}
                        <button type="button" onClick={() => setMembers(prev => prev.filter(x => x !== m))} aria-label={`Remove ${m}`} data-id="element-2638" data-mp="30402lsy91joc">
                          <X className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-2639" data-mp="1om7egrehdzqy" />
                        </button>
                      </span>)}
                  </div>
                </div>)}
            </div>
          </Card>

          <Card title="When" data-id="element-2640" data-mp="1jiogsam0hcon">
            <div className="flex gap-2 mb-4" data-id="element-2641" data-mp="1jhwksyoi5cxu">
              {([['now', 'Send now', Send], ['schedule', 'Schedule', Calendar], ['repeat', 'Repeat', Repeat]] as const).map(([v, l, Icon]) => <button key={v} type="button" onClick={() => setWhen(v)} className={`flex items-center gap-2 px-4 h-10 rounded-md border text-sm font-medium ${when === v ? 'border-[#0EA5E9] bg-[#F0F9FF] text-[#0369A1]' : 'border-[#E2E8F0] text-[#475569]'}`} data-id="element-2642" data-mp="1t8hov1my39o5">
                  <Icon className="w-4 h-4" data-id="element-2643" data-mp="2777mcidofbd1" /> {l}
                </button>)}
            </div>
            {when === 'schedule' && <div className="grid grid-cols-2 gap-4 max-w-md" data-id="element-2644" data-mp="2c6u0cu1bwvmw">
                <div data-id="element-2645" data-mp="2c3y4db91kh13">
                  <FieldLabel data-id="element-2646" data-mp="ctg7n4ghq1aq">Date</FieldLabel>
                  <input type="date" className={inputClass} value={date} onChange={e => setDate(e.target.value)} data-id="element-2647" data-mp="cur9gyqdkyz7" />
                </div>
                <div data-id="element-2648" data-mp="289d6gv03larj">
                  <FieldLabel hint="Store timezone (Melbourne)" data-id="element-2649" data-mp="288vc63wzx6m9">Time</FieldLabel>
                  <input type="time" className={inputClass} value={time} onChange={e => setTime(e.target.value)} data-id="element-2650" data-mp="23aabk3du0kxa" />
                </div>
              </div>}
            {when === 'repeat' && <div className="space-y-4 max-w-xl" data-id="element-2651" data-mp="2h7ywfb7wfs2c">
                <div className="grid grid-cols-2 gap-4" data-id="element-2652" data-mp="2caqe9amr3o13">
                  <div data-id="element-2653" data-mp="2c95jpl0fs538">
                    <FieldLabel data-id="element-2654" data-mp="1okw1et2flzgu">Repeats</FieldLabel>
                    <select className={inputClass} value={repeatType} onChange={e => setRepeatType(e.target.value)} data-id="element-2655" data-mp="1olnkj4eue4q8">
                      <option data-id="element-2656" data-mp="1khzckkmat74g">Every day</option>
                      <option data-id="element-2657" data-mp="1yc8t9ko8cpmv">Weekdays</option>
                      <option data-id="element-2658" data-mp="1yd1s7zgy6aug">Weekends</option>
                      <option data-id="element-2659" data-mp="1tk8qlkgrziml">Weekly on chosen days</option>
                      <option data-id="element-2660" data-mp="1tinv9smmzvno">Monthly on chosen dates</option>
                    </select>
                  </div>
                  <div data-id="element-2661" data-mp="10w30sjav73fw">
                    <FieldLabel hint="Store timezone (Melbourne)" data-id="element-2662" data-mp="10wl3nys18fws">Time</FieldLabel>
                    <input type="time" className={inputClass} value={time} onChange={e => setTime(e.target.value)} data-id="element-2663" data-mp="3cin2ohpexfv9" />
                  </div>
                </div>
                {repeatType === 'Weekly on chosen days' && <div className="flex gap-1.5" data-id="element-2664" data-mp="33j07wer6v8b6">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => {
                const on = repeatDays.includes(d);
                return <button key={d} type="button" onClick={() => setRepeatDays(prev => on ? prev.filter(x => x !== d) : [...prev, d])} className={`w-11 h-9 rounded-md border text-xs font-medium ${on ? 'border-[#0EA5E9] bg-[#F0F9FF] text-[#0369A1]' : 'border-[#E2E8F0] text-[#475569]'}`} data-id="element-2665" data-mp="21br6ah3tgc">
                          {d}
                        </button>;
              })}
                  </div>}
                {repeatType === 'Monthly on chosen dates' && <div data-id="element-2666" data-mp="2at1haexcj34e">
                    <FieldLabel hint="Comma separated, e.g. 1, 15" data-id="element-2667" data-mp="2asi0j1jfmkry">Dates of the month</FieldLabel>
                    <input className={inputClass} defaultValue="1" data-id="element-2668" data-mp="2fu78277g4gba" />
                  </div>}
                <div data-id="element-2669" data-mp="1vq00tl0pd0bm">
                  <FieldLabel data-id="element-2670" data-mp="1vsm61e04f3qo">Ends</FieldLabel>
                  <div className="flex items-center gap-3" data-id="element-2671" data-mp="1rq91uu10vdw0">
                    <SegmentedControl value={endType} onChange={setEndType} options={[{
                  value: 'count',
                  label: 'After a number of sends'
                }, {
                  value: 'date',
                  label: 'On a date'
                }]} data-id="element-2672" data-mp="1myhkna3rakij" />
                    {endType === 'count' ? <input className={`${inputClass} w-24`} value={endCount} onChange={e => setEndCount(e.target.value)} data-id="element-2673" data-mp="181gqz1208fhf" /> : <input type="date" className={`${inputClass} w-44`} defaultValue="2027-02-28" data-id="element-2674" data-mp="13z3nkjaqctnt" />}
                  </div>
                </div>
              </div>}
            <label className="flex items-start gap-2 mt-5 cursor-pointer" data-id="element-2675" data-mp="z2lnvxu9blic">
              <input type="checkbox" checked={alsoEmail} onChange={e => setAlsoEmail(e.target.checked)} className="mt-0.5 rounded text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-2676" data-mp="2yd4kuzqmt8ls" />
              <span data-id="element-2677" data-mp="30yuhjxi392l0">
                <span className="block text-sm text-[#0F172A]" data-id="element-2678" data-mp="30x9okcbf9rp9">Also send as an email</span>
                <span className="block text-xs text-[#64748B]" data-id="element-2679" data-mp="35w45t9tqr879">Off by default. Most wineries keep email in Klaviyo.</span>
              </span>
            </label>
          </Card>

          <div className="flex justify-end gap-2" data-id="element-2680" data-mp="3fngtrqcf5e80">
            <Button variant="ghost" onClick={onCancel} data-id="element-2681" data-mp="3fm5vg61qsyoa">
              Cancel
            </Button>
            <Button variant="primary" disabled={!title || !message || count === 0} onClick={() => setReviewOpen(true)} data-id="element-2682" data-mp="2i11d2qrtgkli">
              Review
            </Button>
          </div>
        </div>

        <div className="xl:sticky xl:top-6" data-id="element-2683" data-mp="2rvtey6sfltm3">
          <Card title="Preview" description="Shown for Sarah Johnson" data-id="element-2684" data-mp="2vyq1u9p08bvu">
            <div className="mb-4" data-id="element-2685" data-mp="2vwvfqs1zydq6">
              <SegmentedControl value={platform} onChange={setPlatform} options={[{
              value: 'apple',
              label: 'Apple'
            }, {
              value: 'google',
              label: 'Google'
            }]} data-id="element-2686" data-mp="77othg2b3kpo" />
            </div>
            <div className="flex justify-center" data-id="element-2687" data-mp="x86k1mtshkvy">
              <LockScreenPreview title={fillPlaceholders(title)} message={fillPlaceholders(message)} platform={platform} data-id="element-2688" data-mp="sbzcjplqw1bw" />
            </div>
            {platform === 'google' && <p className="text-xs text-[#94A3B8] mt-3 m-0" data-id="element-2689" data-mp="129dr1jo3yg1k">
                On Google Wallet the message also shows on the pass for a few days.
              </p>}
          </Card>
        </div>
      </div>

      {reviewOpen && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setReviewOpen(false)} data-id="element-2690" data-mp="1b2gzfdjwc2pc" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none" data-id="element-2691" data-mp="1pz85g5o2scnx">
            <div className="bg-white rounded-lg shadow-xl w-[480px] overflow-hidden pointer-events-auto" data-id="element-2692" data-mp="1l0wt9oab5q2j">
              <div className="p-6" data-id="element-2693" data-mp="3rr4dv6ey56eh">
                <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-2694" data-mp="3rsfjlbryco88">
                  {when === 'now' ? 'Send this notification?' : when === 'repeat' ? 'Set up this repeating notification?' : 'Schedule this notification?'}
                </h3>
                <dl className="space-y-3 m-0 text-sm" data-id="element-2695" data-mp="2778yzkvehrdc">
                  {[['Title', title], ['Audience', audienceLabel], ['Reach', `${count.toLocaleString()} passes, about ${Math.round(count * 0.66)} Apple and ${Math.round(count * 0.37)} Google`], ['When', whenLabel], ['Email', alsoEmail ? 'Also sent as email' : 'Wallet only']].map(([k, v]) => <div key={k} className="grid grid-cols-[90px_1fr] gap-3" data-id="element-2696" data-mp="1jheq1ee7cmyd">
                      <dt className="text-[#64748B]" data-id="element-2697" data-mp="1nl2v9qfd36gg">{k}</dt>
                      <dd className="m-0 text-[#0F172A]" data-id="element-2698" data-mp="19ks5kqpsfxwv">{v}</dd>
                    </div>)}
                </dl>
                {when === 'now' && <p className="text-xs text-[#64748B] mt-4 m-0" data-id="element-2699" data-mp="1eko8snqnvlbd">
                    Sends can't be recalled once they go out.
                  </p>}
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex justify-end gap-2" data-id="element-2700" data-mp="38xe2p5h14uir">
                <Button variant="ghost" size="sm" onClick={() => setReviewOpen(false)} data-id="element-2701" data-mp="gbwk4z3kpknz">
                  Back
                </Button>
                <Button variant="primary" size="sm" onClick={submit} data-id="element-2702" data-mp="l3wn70exwtos">
                  {when === 'now' ? 'Send now' : when === 'repeat' ? 'Set up' : 'Schedule'}
                </Button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}
export function WalletNotificationsTab({
  startComposing,
  onToast
}: {
  startComposing: boolean;
  onToast: (msg: string) => void;
}) {
  const [list, setList] = useState<WalletNotification[]>(walletNotifications);
  const [composing, setComposing] = useState(startComposing);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'sent'>('all');
  const [selected, setSelected] = useState<WalletNotification | null>(null);
  const [cancelTarget, setCancelTarget] = useState<WalletNotification | null>(null);
  if (composing) {
    return <Composer onCancel={() => setComposing(false)} onDone={(n, msg) => {
      setList(prev => [n, ...prev]);
      setComposing(false);
      onToast(msg);
    }} data-id="element-2703" data-mp="3ft4kfpfwnt7f" />;
  }
  const shown = list.filter(n => filter === 'all' ? true : filter === 'upcoming' ? ['Scheduled', 'Repeating', 'Sending'].includes(n.status) : ['Sent', 'Failed', 'Cancelled'].includes(n.status));
  return <div className="space-y-6" data-id="element-2704" data-mp="3c9cjmyi4h7rr">
      <Card title="Notifications" description="Push messages to members' wallet passes. They appear on the lock screen without an app." action={<Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-2707" data-mp="2jlztyfgo0ju8" />} onClick={() => setComposing(true)} data-id="element-2706" data-mp="2jkygk4c6fl9c">
            New notification
          </Button>} data-id="element-2705" data-mp="3caf8l0vvx051">
        <div className="mb-4" data-id="element-2708" data-mp="2ybfmo0zrazck">
          <SegmentedControl value={filter} onChange={setFilter} options={[{
          value: 'all',
          label: 'All'
        }, {
          value: 'upcoming',
          label: 'Upcoming'
        }, {
          value: 'sent',
          label: 'Sent'
        }]} data-id="element-2709" data-mp="2tj4jmp9pyxfs" />
        </div>
        <div className="overflow-x-auto" data-id="element-2710" data-mp="20scqy1rv4qx7">
          <table className="w-full text-sm" data-id="element-2711" data-mp="4ru5wifwqk3t">
            <thead data-id="element-2712" data-mp="4r2e7ipfl6it">
              <tr className="text-left text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px] border-b border-[#E2E8F0]" data-id="element-2713" data-mp="4phqabxmgltx">
                <th className="py-2 pr-4" data-id="element-2714" data-mp="egvo8f9xddky">Notification</th>
                <th className="py-2 pr-4" data-id="element-2715" data-mp="ejhv0cozrp24">Audience</th>
                <th className="py-2 pr-4" data-id="element-2716" data-mp="ihfjteeuo94z">When</th>
                <th className="py-2 pr-4" data-id="element-2717" data-mp="3hxuc70ghlm4p">Status</th>
                <th className="py-2 pr-4 text-right" data-id="element-2718" data-mp="3hz5o5nk3606w">Passes</th>
                <th className="py-2 pr-4 text-right" data-id="element-2719" data-mp="3mwfbmxnx01s3">Sends (Apple / Google)</th>
                <th className="py-2" data-id="element-2720" data-mp="3qyipfsy23igj" />
              </tr>
            </thead>
            <tbody data-id="element-2721" data-mp="3r0n3v4tkzzv2">
              {shown.map(n => <tr key={n.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer" onClick={() => setSelected(n)} data-id="element-2722" data-mp="3vxdfnfycs9ao">
                  <td className="py-3 pr-4 max-w-[280px]" data-id="element-2723" data-mp="167i9xoz7f50y">
                    <div className="font-medium text-[#0F172A] truncate" data-id="element-2724" data-mp="1b74mrxb4ej98">{n.title}</div>
                    <div className="text-xs text-[#64748B] truncate" data-id="element-2725" data-mp="1lbbjnn1z2tgu">{n.message}</div>
                  </td>
                  <td className="py-3 pr-4 text-[#475569]" data-id="element-2726" data-mp="1gci84068xcjb">{n.audience}</td>
                  <td className="py-3 pr-4 text-[#475569] whitespace-nowrap" data-id="element-2727" data-mp="1gektu80d8ji8">
                    {n.when}
                    {n.repeat && <div className="text-xs text-[#94A3B8]" data-id="element-2728" data-mp="17k6g49vhhh18">{n.repeat}</div>}
                  </td>
                  <td className="py-3 pr-4" data-id="element-2729" data-mp="12kk58734ocvl">
                    <StatusPill status={n.status} data-id="element-2730" data-mp="12meqjmiba707" />
                  </td>
                  <td className="py-3 pr-4 text-right tabular-nums text-[#0F172A]" data-id="element-2731" data-mp="xlh7z8te3ay6">{n.passes.toLocaleString()}</td>
                  <td className="py-3 pr-4 text-right tabular-nums text-[#475569]" data-id="element-2732" data-mp="snffjxa2pza1">
                    {n.status === 'Sent' ? `${n.appleSends} / ${n.googleSends}` : '–'}
                  </td>
                  <td className="py-3 text-right" onClick={e => e.stopPropagation()} data-id="element-2733" data-mp="2mznhhuktbgfb">
                    {['Scheduled', 'Repeating'].includes(n.status) && <Button variant="ghost" size="sm" onClick={() => setCancelTarget(n)} data-id="element-2734" data-mp="2iwz4rbsz9pd2">
                        Cancel
                      </Button>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#94A3B8] mt-4 m-0" data-id="element-2735" data-mp="2h24yhehi4arl">
          A member with the pass in both Apple and Google wallets gets two sends, so sends can be higher than passes.
        </p>
      </Card>

      {selected && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setSelected(null)} data-id="element-2736" data-mp="27b0hk989mfu4" />
          <div className="fixed top-0 right-0 h-full w-[440px] bg-white shadow-xl z-50 flex flex-col" data-id="element-2737" data-mp="2c3aei97ssg5w">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-2738" data-mp="1xexduyxrm952">
              <h2 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-2739" data-mp="22dh4hcnb093r">Notification</h2>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-md hover:bg-[#F1F5F9]" aria-label="Close" data-id="element-2740" data-mp="1odza8hr8o3n0">
                <X className="w-5 h-5 text-[#64748B]" data-id="element-2741" data-mp="1tb7ssjgau9os" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-2742" data-mp="19qr25pt88ef9">
              <div className="flex justify-center" data-id="element-2743" data-mp="14rxs67d5f5ju">
                <LockScreenPreview title={fillPlaceholders(selected.title)} message={fillPlaceholders(selected.message)} platform="apple" data-id="element-2744" data-mp="14th6vtj5p8ji" />
              </div>
              <dl className="space-y-3 m-0 text-sm" data-id="element-2745" data-mp="1eoivmo9is8lk">
                {[['Status', <StatusPill status={selected.status} data-id="element-2746" data-mp="1sotlbnz3fh55" />], ['Audience', selected.audience], ['When', selected.when], ['Repeat', selected.repeat || 'No'], ['Passes', selected.passes.toLocaleString()], ['Apple sends', selected.status === 'Sent' ? selected.appleSends : '–'], ['Google sends', selected.status === 'Sent' ? selected.googleSends : '–']].map(([k, v]) => <div key={k as string} className="grid grid-cols-[110px_1fr] gap-3" data-id="element-2747" data-mp="1xgbomiol9tta">
                    <dt className="text-[#64748B]" data-id="element-2748" data-mp="2bgcru4sbtdbx">{k}</dt>
                    <dd className="m-0 text-[#0F172A]" data-id="element-2749" data-mp="26grrpr1f6yxm">{v}</dd>
                  </div>)}
              </dl>
            </div>
            {['Scheduled', 'Repeating'].includes(selected.status) && <div className="px-6 py-4 border-t border-[#E2E8F0] flex justify-end" data-id="element-2750" data-mp="14191fu5rqf7p">
                <Button variant="danger" size="sm" onClick={() => {
            setCancelTarget(selected);
            setSelected(null);
          }} data-id="element-2751" data-mp="1902ejlh5847c">
                  Cancel notification
                </Button>
              </div>}
          </div>
        </>}

      {cancelTarget && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setCancelTarget(null)} data-id="element-2752" data-mp="1mtbktd9so9ln" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none" data-id="element-2753" data-mp="1rqtked1x9cgz">
            <div className="bg-white rounded-lg shadow-xl w-[420px] p-6 pointer-events-auto" data-id="element-2754" data-mp="1wsj04603aebe">
              <h3 className="text-base font-semibold text-[#0F172A] mb-2" data-id="element-2755" data-mp="20sjlybpiprsh">Cancel this notification?</h3>
              <p className="text-sm text-[#475569] mb-5" data-id="element-2756" data-mp="2ft33fefcozs">
                {cancelTarget.status === 'Repeating' ? 'No more sends will go out. Sends already delivered stay in members\' notification history.' : 'It won\'t be sent. You can create it again later.'}
              </p>
              <div className="flex justify-end gap-2" data-id="element-2757" data-mp="3q8x1om19wl0o">
                <Button variant="ghost" size="sm" onClick={() => setCancelTarget(null)} data-id="element-2758" data-mp="3lb3v3rw25ozv">
                  Keep it
                </Button>
                <Button variant="danger" size="sm" onClick={() => {
              setList(prev => prev.map(n => n.id === cancelTarget.id ? {
                ...n,
                status: 'Cancelled'
              } : n));
              setCancelTarget(null);
              onToast('Notification cancelled');
            }} data-id="element-2759" data-mp="3hb3afpid8hkd">
                  Cancel notification
                </Button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}